'use client';
import React, { useMemo, useState } from 'react';
import { Icon } from '@benzinga/core-ui';
import { JsonLd } from '@benzinga/seo';
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube';
import type { QuoteVideoRecord } from '@benzinga/videos-manager';
import { YouTubeVideoPlayer } from './YouTubeVideoPlayer';
import { buildVideoObjectSchema } from './buildVideoObjectSchema';

const BENZINGA_SUBSCRIBE_URL = 'https://www.youtube.com/user/BenzingaTV?sub_confirmation=1';
const MAX_HASHTAGS = 6;

export interface QuoteVideoSectionProps {
  records: QuoteVideoRecord[];
  symbol?: string;
}

const extractHashtags = (description: string): string[] => {
  const tags: string[] = [];
  const seen = new Set<string>();
  for (const match of description.matchAll(/#(\w+)/g)) {
    const tag = match[1];
    const key = tag.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      tags.push(tag);
    }
    if (tags.length >= MAX_HASHTAGS) break;
  }
  return tags;
};

const formatPublishedDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const QuoteVideoSection: React.FC<QuoteVideoSectionProps> = ({ records, symbol }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const safeIndex = selectedIndex < (records?.length ?? 0) ? selectedIndex : 0;
  const selected = records?.[safeIndex];
  const hashtags = useMemo(() => (selected ? extractHashtags(selected.description) : []), [selected]);

  if (!records || records.length === 0 || !selected) {
    return null;
  }

  const heading = symbol ? `${symbol.toUpperCase()} Videos` : 'Videos';
  const publishedDate = formatPublishedDate(selected.publishedAt);
  const otherVideos = records.map((video, index) => ({ index, video })).filter(item => item.index !== safeIndex);

  return (
    <section
      aria-label={heading}
      className="quote-video-section flex w-full min-w-0 flex-col overflow-hidden !rounded-lg !border !border-solid !border-[#CEDDF2] bg-white"
    >
      <JsonLd data={buildVideoObjectSchema(selected)} id={`video-object-schema-${selected.videoId}`} />

      <header className="flex min-h-[3.25rem] flex-wrap items-center justify-between gap-3 border-b border-[#E1EBFA] bg-gradient-to-b from-[#F3F9FF] to-white px-[1.125rem] py-3.5">
        <h2 className="m-0 min-w-0 text-base font-bold leading-[1.3] tracking-[-0.01em] text-[#192940]">{heading}</h2>
        <a
          aria-label="Subscribe to Benzinga on YouTube"
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#ff0000] px-3 py-1 text-xs font-semibold !text-white no-underline transition-colors hover:bg-[#cc0000]"
          href={BENZINGA_SUBSCRIBE_URL}
          rel="noopener noreferrer"
          target="_blank"
        >
          <Icon icon={faYoutube} />
          Subscribe
        </a>
      </header>

      <div className="flex flex-col gap-3 px-[1.125rem] pb-4 pt-3">
        <YouTubeVideoPlayer title={selected.title} videoId={selected.videoId} />

        <div>
          <h3 className="m-0 text-[0.9375rem] font-semibold leading-snug text-[#0f1b2d]">{selected.title}</h3>
          {publishedDate && <div className="mt-1 text-xs text-[#667085]">{publishedDate}</div>}
        </div>

        {hashtags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {hashtags.map(tag => (
              <a
                className="rounded-full bg-[#f2f4f7] px-2.5 py-0.5 text-xs font-medium text-[#344054] no-underline transition-colors hover:bg-[#e4e8ee] hover:text-[#0f1b2d]"
                href={`https://www.youtube.com/hashtag/${tag.toLowerCase()}`}
                key={tag}
                rel="noopener noreferrer"
                target="_blank"
              >
                #{tag}
              </a>
            ))}
          </div>
        )}

        {otherVideos.length > 0 && (
          <div className="border-t border-[#f2f4f7] pt-3">
            <h4 className="m-0 mb-2.5 text-xs font-semibold uppercase tracking-[0.04em] text-[#667085]">
              More {symbol ? symbol.toUpperCase() : ''} videos
            </h4>
            <ul className="m-0 flex max-h-60 list-none flex-col gap-3 overflow-y-auto p-0">
              {otherVideos.map(({ index, video }) => (
                <li key={video.videoId}>
                  <button
                    className="flex w-full items-start gap-3 rounded-md border-0 bg-transparent p-1 text-left transition-colors hover:bg-[#f9fafb]"
                    onClick={() => setSelectedIndex(index)}
                    type="button"
                  >
                    <img
                      alt=""
                      className="aspect-video w-24 shrink-0 rounded-md object-cover"
                      loading="lazy"
                      src={video.thumbnailUrl || `https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg`}
                    />
                    <span className="line-clamp-3 text-[0.8125rem] font-medium leading-[1.4] text-[#0f1b2d]">
                      {video.title}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};
